import type { FieldValue, FieldValueType } from "../../../types";
import type { FieldId } from "../../../types";
import { prepareFieldValuesForSubmission } from "../../../utils/field-collection-utils";
import { createInitialValues } from "../../../utils/field-utils";
import type {
  FormStore,
  LifecycleActions,
  SliceSetFunction,
} from "../../types";

/**
 * フォームライフサイクルに関するアクション
 * - リセット
 * - 送信
 * - 送信状態管理
 */
export const createLifecycleActions = (
  set: SliceSetFunction,
  get: () => FormStore,
): LifecycleActions => ({
  /**
   * フォームをリセット
   * キャンセルボタン押下時やフォームの初期値をリセットするために使用する
   */
  reset: () => {
    const { form, initialValueProvider } = get();
    if (!form) {
      console.warn("リセットするフォーム定義がありません");
      return;
    }
    if (!initialValueProvider) {
      console.warn("初期値プロバイダーがありません");
      return;
    }
    const initialFieldValues = createInitialValues(form, initialValueProvider);

    set(
      {
        values: initialFieldValues,
        errors: [],
        touched: {},
        isSubmitted: false,
        isSubmitAttempted: false,
      },
      false,
      "lifecycle/reset",
    );
  },

  /**　フォーム送信 */
  submit: async (
    onSuccess?: (values: FieldValue[]) => Promise<void> | void,
  ) => {
    // 送信試行済みに設定
    set({ isSubmitAttempted: true }, false, "lifecycle/setSubmitAttempted");

    // バリデーションを実行
    const isValid = get().validateAll();

    if (!isValid) {
      return;
    }

    // 送信中に設定
    set({ isSubmitting: true }, false, "lifecycle/setSubmitting");

    try {
      // 無効化されたコレクションのフィールドは値配列から既に削除されているはず
      const { values } = get();

      // 空の値を持つフィールドを除外（ただし空配列は保持）
      const preparedValues = prepareFieldValuesForSubmission(values);

      // 成功コールバックが指定されている場合は実行
      if (onSuccess) {
        await onSuccess(preparedValues);
      }

      //　送信時の値を後続編集時点の初期値として保持しておく(キャンセル押下時のUIに反映するため)
      // NOTE: API側が非同期で値を更新する場合や、後続のAPIレスポンスで値が最新化されていない場合でも、UIは楽観的更新をおこなうため送信時の値を初期値として保持しておく
      const provider = get().initialValueProvider;
      if (provider) {
        const newInitial = preparedValues.reduce(
          (acc: Record<FieldId, FieldValueType>, item) => {
            acc[item.fieldId] = item.value;
            return acc;
          },
          {},
        );
        provider.updateInitialValues(newInitial);
      }
      // 送信完了に設定
      set(
        { isSubmitted: true, isSubmitting: false },
        false,
        "lifecycle/submitSuccess",
      );
    } catch (error) {
      // エラー時は送信中を解除
      set({ isSubmitting: false }, false, "lifecycle/submitError");
      throw error;
    }
  },

  /**　送信中状態を設定　*/
  setIsSubmitting: (isSubmitting: boolean) => {
    set({ isSubmitting }, false, "lifecycle/setIsSubmitting");
  },

  /** 送信試行状態を設定 */
  setIsSubmitAttempted: (isSubmitAttempted: boolean) => {
    set({ isSubmitAttempted }, false, "lifecycle/setIsSubmitAttempted");
  },
});
